import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, User, ChefHat } from 'lucide-react';
import { toast } from 'react-toastify';
import styles from './SignUpPage.module.css';

export function SignUpPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }
    console.log('Signup:', formData);
    navigate('/profile');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className={styles.container}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={styles.cardWrapper}>
        <div className={styles.header}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className={styles.logoCircle}
          >
            <ChefHat className={styles.logoIcon} />
          </motion.div>
          <h2 className={styles.title}>Join Food Box</h2>
          <p className={styles.subtitle}>Create your account and start cooking</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={styles.formCard}
        >
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Full Name */}
            <div>
              <label htmlFor="name" className={styles.label}>Full Name</label>
              <div className={styles.inputWrapper}>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="jyotika mehra"
                  className={styles.inputField}
                />
                <User className={styles.inputIcon} />
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={styles.label}>Email Address</label>
              <div className={styles.inputWrapper}>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  className={styles.inputField}
                />
                <Mail className={styles.inputIcon} />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className={styles.label}>Password</label>
              <div className={styles.inputWrapper}>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className={styles.inputField}
                />
                <Lock className={styles.inputIcon} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={styles.showPasswordBtn}
                >
                  {showPassword ? <EyeOff className={styles.eyeIcon} /> : <Eye className={styles.eyeIcon} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className={styles.label}>Confirm Password</label>
              <div className={styles.inputWrapper}>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  className={styles.inputField}
                />
                <Lock className={styles.inputIcon} />
              </div>
            </div>

            {/* Terms */}
            <div className={styles.termsWrapper}>
              <input id="terms" type="checkbox" required className={styles.checkbox} />
              <label htmlFor="terms" className={styles.termsLabel}>
                I agree to the{' '}
                <a href="#" className={styles.link}>Terms of Service</a> and{' '}
                <a href="#" className={styles.link}>Privacy Policy</a>
              </label>
            </div>

            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={styles.submitBtn}
            >
              Create Account
            </motion.button>
          </form>

          <div className={styles.signInWrapper}>
            <p className={styles.signInText}>
              Already have an account?{' '}
              <Link to="/login" className={styles.signInLink}>Sign in</Link>
            </p>
          </div>

          <div className={styles.orDivider}>
            <div className={styles.borderLine}></div>
            <div className={styles.orText}>Or sign up with</div>
          </div>

          <div className={styles.socialBtns}>
            <button className={styles.socialBtn}>
              <svg className={styles.socialIcon} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className={styles.socialText}>Google</span>
            </button>
            <button className={styles.socialBtn}>
              <svg className={styles.socialIcon} fill="#1877F2" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              <span className={styles.socialText}>Facebook</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}